import { Button, Group, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { businessToday, type ClientFiledDossierDto, type ClientRcUploadInput, type FileRefDto } from '@mexp/shared';
import { http } from '@/api/client';
import { FileUploader } from '@/features/files/FileUploader';
import { toBusinessDate } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';
import { portalKeys } from './api';

/** The client uploads the registration certificate of one dossier, with its issue and expiry dates. */
export function RcUploadModal({ dossier, onClose }: { dossier: ClientFiledDossierDto | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [issueDate, setIssueDate] = useState<string | null>(null);
  const [expiryDate, setExpiryDate] = useState<string | null>(null);

  useEffect(() => {
    setFiles([]);
    setIssueDate(null);
    setExpiryDate(null);
  }, [dossier?.id]);

  const today = businessToday();
  const dateError =
    issueDate && issueDate > today
      ? 'The issue date cannot be in the future'
      : issueDate && expiryDate && expiryDate <= issueDate
        ? 'The expiry date must be after the issue date'
        : expiryDate && expiryDate < today
          ? 'This certificate has already expired. Upload the one that is valid now.'
          : null;

  const save = useMutation({
    mutationFn: () => {
      const body: ClientRcUploadInput = {
        dossierEntryId: dossier!.request ? null : dossier!.id,
        requestId: dossier!.request?.id ?? null,
        fileId: files[0]!.id,
        issueDate: issueDate!,
        expiryDate: expiryDate!,
      };
      return http.post('/portal/registration-certificates', body);
    },
    onSuccess: async () => {
      await Promise.all([portalKeys.library, portalKeys.summary].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      notifySuccess('Registration certificate added');
      onClose();
    },
    onError: (e) => notifyError(e, 'The certificate was not added'),
  });

  const renewing = dossier ? dossier.rcStatus !== 'missing' : false;
  return (
    <Modal opened={!!dossier} onClose={onClose} title={renewing ? 'Upload the renewed registration certificate' : 'Upload the registration certificate'} size="lg">
      {dossier && (
        <Stack gap="md">
          <Stack gap={2}>
            <Text fw={600}>
              {dossier.product ?? dossier.name}
              {dossier.country && (
                <Text span c="dimmed" fw={400} inherit>
                  {' '}
                  for {dossier.country}
                </Text>
              )}
            </Text>
            <Text fz="xs" c="dimmed">
              {dossier.request ? `Dossier ${dossier.request.code}` : dossier.name}
            </Text>
          </Stack>
          <FileUploader
            area="data_bank"
            context={{ section: 'registration' }}
            value={files}
            onChange={(next) => setFiles(next.slice(-1))}
            multiple={false}
            label="Drop the certificate here or click to choose"
            hint="PDF or an image of the certificate as issued by the authority"
            onBusyChange={setBusy}
          />
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <DateInput
              label="Issue date"
              required
              valueFormat="DD MMM YYYY"
              maxDate={today}
              value={issueDate}
              onChange={(v) => setIssueDate(toBusinessDate(v))}
            />
            <DateInput
              label="Expiry date"
              required
              valueFormat="DD MMM YYYY"
              minDate={issueDate && issueDate > today ? issueDate : today}
              value={expiryDate}
              onChange={(v) => setExpiryDate(toBusinessDate(v))}
              error={dateError}
            />
          </SimpleGrid>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={busy || !files.length || !issueDate || !expiryDate || !!dateError}>
              Add certificate
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
